import CalendarToday from '@mui/icons-material/CalendarToday';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useMemo } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { getUrlHealthCheckFilesDownload } from 'api/ApiUrls';
import { useProductCenterApi } from 'api/ApplicationApi';
import {
	healthCheckFilesQuery,
	healthCheckKeys,
} from 'api/queries/HealthCheckQueries';
import type { FileSetDto } from 'api/v1/data-contracts';
import UserIcon from 'assets/volvoIcons/People/UserIcon';
import { FileDisplayBase } from 'library/components/controls/files/FileDisplayBase';
import { getFileType } from 'library/components/controls/files/functions';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';

import { HealthCheckFileRemoveButton } from './HealthCheckFileRemoveButton';

type HealthCheckFilesTableProps = {
	canWrite: boolean;
	canAdmin: boolean;
	unitId: number;
};

const iconAlignmentSx = {
	'.server-file-display_comment-icon, .server-file-display_calendar-icon, .server-file-display_user-icon':
		{
			verticalAlign: 'bottom',
			mr: 1,
		},
} as const;

const normalizeComment = (value: string | null | undefined) =>
	(value ?? '').trim();

const toTimestamp = (value: Date): number => {
	const time = new Date(value).getTime();
	return Number.isFinite(time) ? time : 0;
};

export const HealthCheckFilesTable = ({
	canWrite,
	canAdmin,
	unitId,
}: HealthCheckFilesTableProps): React.JSX.Element => {
	const { t } = useTranslation(['common', 'component']);
	const { api } = useProductCenterApi();
	const queryClient = useQueryClient();
	const user = useAppSelector(selectUser);
	const {
		data,
		isLoading: filesLoading,
		isError: filesError,
	} = useQuery({
		...healthCheckFilesQuery(api, unitId),
	});

	const handleDeleted = async () => {
		await queryClient.invalidateQueries({
			queryKey: healthCheckKeys.files(unitId),
		});
	};

	const fileSets = useMemo<FileSetDto[]>(() => {
		const sets = [
			...(data?.initialFileSet ? [data.initialFileSet] : []),
			...(data?.additionalFileSets ?? []),
		];
		return sets
			.slice()
			.sort(
				(a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp)
			);
	}, [data]);

	const canRemoveFile = (fileSet: FileSetDto) => {
		if (canAdmin) return true;
		return (
			canWrite &&
			fileSet.submittedByCustomerId ===
				user.customer?.customerNumber?.toString()
		);
	};

	let content: React.ReactNode;
	if (filesError) {
		content = (
			<Alert severity="warning">
				{t('component:failed-to-load-files')}
			</Alert>
		);
	} else if (filesLoading) {
		content = (
			<Typography variant="body2" color="text.secondary">
				{t('common:loading-placeholder')}
			</Typography>
		);
	} else if (fileSets.length === 0) {
		content = (
			<Typography variant="body2" color="text.secondary">
				{t('component:no-files-uploaded-yet')}
			</Typography>
		);
	} else {
		content = (
			<Box>
				{fileSets.map((fileSet, index) => (
					<Fragment key={fileSet.id}>
						{index > 0 && <Divider sx={{ my: 2 }} />}
						<Box
							className="server-file-display_root"
							sx={{
								display: 'grid',
								alignItems: 'center',
								gap: 2,
								gridTemplateAreas: {
									xs: `"files files"
									"comment comment"
									"date user"`,
									md: '"files comment date user"',
								},
								gridTemplateColumns: {
									xs: '1fr 1fr',
									md: '6fr 7fr 2fr 3fr',
								},
								...iconAlignmentSx,
							}}>
							<Stack
								direction="column"
								spacing={2}
								sx={{ gridArea: 'files' }}>
								{fileSet.files.map((file) => (
									<Box key={file.id} sx={{ display: 'flex' }}>
										<FileDisplayBase
											name={file.fileName}
											url={getUrlHealthCheckFilesDownload(
												file.id
											)}
											type={
												getFileType(file.fileName) ?? ''
											}
										/>
										{canRemoveFile(fileSet) && (
											<HealthCheckFileRemoveButton
												unitId={unitId}
												fileId={file.id}
												onDeleted={handleDeleted}
											/>
										)}
									</Box>
								))}
							</Stack>
							<Box
								sx={{
									gridArea: 'comment',
									whiteSpace: 'pre-wrap',
									display: 'inline-flex',
									alignItems: 'center',
								}}>
								{!normalizeComment(fileSet.comment) && (
									<ChatBubbleOutline
										className="server-file-display_comment-icon"
										color="disabled"
									/>
								)}
								<Typography
									component="span"
									sx={{
										ml: normalizeComment(fileSet.comment)
											? 0
											: 1,
									}}>
									{fileSet.comment ?? ''}
								</Typography>
							</Box>
							<Typography sx={{ gridArea: 'date' }}>
								<CalendarToday
									className="server-file-display_calendar-icon"
									color="disabled"
								/>
								{getLocalizedDateFormat(fileSet.timestamp)}
							</Typography>
							<Typography sx={{ gridArea: 'user' }}>
								<UserIcon
									className="server-file-display_user-icon"
									color="primary"
									opacity={0.4}
								/>
								{fileSet.submittedBy ?? ''}
							</Typography>
						</Box>
					</Fragment>
				))}
			</Box>
		);
	}

	return <Box>{content}</Box>;
};
